import React, { useState } from 'react';
import { ChartSetList } from '../../types';

interface SetlistDetailProps {
  collection: ChartSetList;
  onUpdateCollection: (id: string, updates: Partial<ChartSetList>) => void;
  onRemoveChart: (collectionId: string, chartTitle: string) => void;
}

const SetlistDetail: React.FC<SetlistDetailProps> = ({
  collection,
  onUpdateCollection,
  onRemoveChart
}) => {
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [dropZoneIndex, setDropZoneIndex] = useState<number | null>(null);

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    (e.currentTarget as HTMLElement).style.opacity = '0.4';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).style.opacity = '1';
    setDraggedItemIndex(null);
    setDropZoneIndex(null);
  };

  const handleDropZoneEnter = (insertIndex: number) => {
    setDropZoneIndex(insertIndex);
  };

  const handleDropZoneLeave = () => {
    setDropZoneIndex(null);
  };

  const handleDropZoneDrop = (e: React.DragEvent, insertIndex: number) => {
    e.preventDefault();
    
    if (draggedItemIndex === null) return;
    
    let newPosition = insertIndex;
    if (draggedItemIndex < insertIndex) {
      newPosition = insertIndex - 1;
    }
    
    if (newPosition === draggedItemIndex) {
      setDraggedItemIndex(null);
      setDropZoneIndex(null);
      return;
    }
    
    const items = [...collection.chartTitles];
    const draggedItem = items[draggedItemIndex];
    
    items.splice(draggedItemIndex, 1);
    items.splice(newPosition, 0, draggedItem);
    
    onUpdateCollection(collection.id, { chartTitles: items });
    
    setDraggedItemIndex(null);
    setDropZoneIndex(null);
  };

  return (
    <div className="flex-none sm:flex-1 flex flex-col max-h-[48vh] sm:max-h-none overflow-hidden">
      <div className="flex-shrink-0 mb-1">
        <div className="flex items-center justify-between">
          <h4 className="text-sm sm:text-lg font-semibold text-white">
            Charts in Setlist ({collection.chartTitles.length})
          </h4>
          {collection.chartTitles.length > 1 && (
            <span className="text-xs sm:text-sm text-gray-400 hidden sm:inline">Drag to reorder</span>
          )}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto border border-gray-700 rounded-lg p-2 sm:p-3 bg-gray-750">
        {collection.chartTitles.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No charts in this setlist yet</p>
        ) : (
          <div>
            {collection.chartTitles.map((chartTitle, index) => (
              <div key={chartTitle}>
                {/* Drop zone before each item */}
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    handleDropZoneEnter(index);
                  }}
                  onDragLeave={handleDropZoneLeave}
                  onDrop={(e) => handleDropZoneDrop(e, index)}
                  className={`transition-all duration-200 ${
                    dropZoneIndex === index && draggedItemIndex !== null
                      ? 'h-8 mb-2 bg-purple-500/20 border-2 border-purple-400 border-dashed rounded-lg'
                      : 'h-1'
                  }`}
                />

                {/* Chart Item */}
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center justify-between p-1.5 sm:p-2 rounded-md cursor-move transition-all mb-1.5 sm:mb-2 ${
                    draggedItemIndex === index
                      ? 'bg-gray-600 opacity-40 transform scale-95'
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  style={{
                    transform: draggedItemIndex === index ? 'scale(0.95)' : 'scale(1)'
                  }}
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="flex flex-col gap-0.5 text-gray-400">
                      <div className="w-0.5 h-0.5 sm:w-1 sm:h-1 bg-current rounded-full"></div>
                      <div className="w-0.5 h-0.5 sm:w-1 sm:h-1 bg-current rounded-full"></div>
                      <div className="w-0.5 h-0.5 sm:w-1 sm:h-1 bg-current rounded-full"></div>
                      <div className="w-0.5 h-0.5 sm:w-1 sm:h-1 bg-current rounded-full"></div>
                      <div className="w-0.5 h-0.5 sm:w-1 sm:h-1 bg-current rounded-full"></div>
                      <div className="w-1 h-1 bg-current rounded-full"></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs sm:text-sm text-gray-400 font-mono min-w-[1.5rem] sm:min-w-[2rem]">
                        {(index + 1).toString().padStart(2, '0')}.
                      </span>
                      <span className="text-sm sm:text-base text-white font-semibold">{chartTitle}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveChart(collection.id, chartTitle);
                    }}
                    className="p-0.5 sm:p-1 text-gray-400 hover:text-red-400 transition-colors flex-shrink-0"
                    title="Remove from setlist"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
            
            {/* Drop zone after last item */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                handleDropZoneEnter(collection.chartTitles.length);
              }}
              onDragLeave={handleDropZoneLeave}
              onDrop={(e) => handleDropZoneDrop(e, collection.chartTitles.length)}
              className={`transition-all duration-200 ${
                dropZoneIndex === collection.chartTitles.length && draggedItemIndex !== null
                  ? 'h-8 bg-purple-500/20 border-2 border-purple-400 border-dashed rounded-lg'
                  : 'h-1'
              }`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SetlistDetail;
